import React from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

/**
 * Service detail page (#service/:slug) – SLS-style deep dive.
 */
export default function ServiceDetail({ slug, content, onBack, onOpenServices, onContact }) {
  const details = content?.servicesDetail || {};
  const overlay = content?.servicesOverlay || {};
  const service = slug ? details[slug] : null;

  const group =
    Array.isArray(overlay.groups) &&
    overlay.groups.find((g) =>
      (g.items || []).some((item) => item.slug === slug)
    );

  const related =
    group && Array.isArray(group.items)
      ? group.items.filter((item) => item.slug !== slug).slice(0, 4)
      : [];

  if (!service) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 font-body font-semibold text-cyan hover:text-cyan/90 mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
          <p className="font-body text-text-sub">Service not found.</p>
        </div>
      </div>
    );
  }

  const { title, introduction, description, features, imageUrl } = service;

  return (
    <div className="min-h-screen bg-forest-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 0%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 90% 80%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan transition text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <span className="text-text-muted">/</span>
          <button
            type="button"
            onClick={onOpenServices}
            className="font-body font-medium text-text-sub hover:text-cyan transition text-sm"
          >
            Services
          </button>
          {group?.name && (
            <>
              <span className="text-text-muted">/</span>
              <span className="font-body text-sm text-cyan">{group.name}</span>
            </>
          )}
        </div>

        <header className="mb-10">
          {group?.name && (
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-3">
              {group.name}
            </p>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-5 leading-tight tracking-tight">
            {title}
          </h1>
          {introduction && (
            <p className="font-body text-lg text-text-body leading-relaxed">{introduction}</p>
          )}
        </header>

        {imageUrl && (
          <figure
            className="mb-10 rounded-xl overflow-hidden border"
            style={{ borderColor: 'var(--jb-border)' }}
          >
            <img src={imageUrl} alt="" className="w-full h-auto" />
          </figure>
        )}

        {description && (
          <section className="mb-10">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-3">How we help</h2>
            <p className="font-body text-text-sub leading-relaxed">{description}</p>
          </section>
        )}

        {Array.isArray(features) && features.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-4">What we cover</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {features.map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl border bg-forest-card"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <span
                    className="mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--jb-cyan-dim)' }}
                  >
                    <Check className="w-3.5 h-3.5 text-cyan" />
                  </span>
                  <span className="font-body text-sm text-text-body leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {related.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-4">
              Related in {group.name}
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((item) => (
                <a
                  key={item.slug}
                  href={`#service/${item.slug}`}
                  className="block p-4 rounded-xl border bg-forest-card hover:border-cyan/50 transition group"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <span className="font-display font-bold text-sm text-text-hero group-hover:text-cyan transition">
                    {item.title}
                  </span>
                  {item.shortDescription && (
                    <p className="font-body text-xs text-text-sub mt-1 leading-relaxed">
                      {item.shortDescription}
                    </p>
                  )}
                </a>
              ))}
            </div>
          </section>
        )}

        <section
          className="p-8 rounded-2xl border text-center"
          style={{ background: 'var(--jb-connect-bg)', borderColor: 'var(--jb-connect-border)' }}
        >
          <h2 className="font-display font-black text-xl sm:text-2xl text-text-hero mb-2">
            Ready to start with {title}?
          </h2>
          <p className="font-body text-text-sub text-sm mb-6 max-w-md mx-auto">
            Book a discovery call — we will map the approach, timeline, and how we embed with your team.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={onContact}
              className="jb-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
            >
              Book a discovery call
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onOpenServices}
              className="px-6 py-3 rounded-xl border font-body font-semibold text-sm text-text-body hover:border-cyan hover:text-cyan transition"
              style={{ borderColor: 'var(--jb-border)' }}
            >
              All services
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
